import { ThemeDirection } from 'config';
import useConfig from 'hooks/useConfig';
import useLocalStorage from 'hooks/useLocalStorage';
import React, { createContext, useState } from 'react';
import { I18n, isI18n } from 'types/config';
import heMessages from 'utils/locales/he.json';
import enMessages from 'utils/locales/en.json';

// const translations = {
//   en,
//   he,
// };
type Messages = Record<string, any>;
interface ILocalizationContext {
  currentLanguage: I18n;
  currentLocale: Messages;
  switchLanguage: (language: I18n) => void;
  languageDirection: ThemeDirection;
}
type LocalizationLanguageProviderType = {
  children: React.ReactNode;
};

function loadLocaleData(i18n: I18n) {
  switch (i18n) {
    case 'en':
      return enMessages;
    default:
      return heMessages;
  }
}

const initialState: ILocalizationContext = {
  currentLanguage: 'he',
  currentLocale: heMessages,
  switchLanguage: () => {},
  languageDirection: ThemeDirection.RTL,
};

const LocalizationContext = createContext(initialState);

const LocalizationLanguageProvider: React.FC<LocalizationLanguageProviderType> = ({ children }) => {
  const [savedLanguageInLocal, saveLanguageToLocal] = useLocalStorage('appLanguage', 'he');
  const { savedLanguage, langFromParams } = React.useMemo(() => {
    const getParams = new URLSearchParams(document.location.search);
    let langFromParams: I18n = isI18n(getParams.get('lang')) ? (getParams.get('lang')! as I18n) : 'he';
    const savedLanguage: I18n = langFromParams ?? savedLanguageInLocal;
    return {
      savedLanguage,
      langFromParams,
    };
  }, [savedLanguageInLocal]);

  const { i18n } = useConfig();

  const [currentLanguage, setCurrentLanguage] = useState<I18n>(savedLanguage);
  const [currentLocale, setCurrentLocale] = useState<Messages>(initialState.currentLocale);
  const [languageDirection, setLanguageDirection] = useState<ThemeDirection>(
    savedLanguage === 'he' ? ThemeDirection.RTL : ThemeDirection.LTR,
  );

  const switchLanguage = React.useCallback(
    async (language: I18n) => {
      const locale = loadLocaleData(language);
      if (locale) {
        setCurrentLanguage(language);
        setCurrentLocale(locale);
        setLanguageDirection(language === 'he' ? ThemeDirection.RTL : ThemeDirection.LTR);
        saveLanguageToLocal(language);
        if (language === 'he') {
          //TODO if (applicationType === 'web') document.documentElement.dir = 'rtl';
          document.documentElement.dir = 'rtl';
        } else {
          //TODO if (applicationType === 'web') document.documentElement.dir = 'ltr';
          document.documentElement.dir = 'ltr';
        }
      } else {
        console.warn(`Language ${language} is not supported`);
      }
    },
    [saveLanguageToLocal],
  );

  React.useEffect(() => {
    //TODO if (applicationType === 'web') document.documentElement.dir = 'ltr';
    document.documentElement.dir = savedLanguage === 'he' ? 'rtl' : 'ltr';

    if (langFromParams) {
      saveLanguageToLocal(langFromParams);
    }
  }, []);

  React.useEffect(() => {
    if (i18n !== currentLanguage) {
      switchLanguage(i18n);
    }
  }, [i18n, currentLanguage, switchLanguage]);

  const value = {
    currentLanguage,
    currentLocale,
    switchLanguage,
    languageDirection,
  };

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
};

export { LocalizationLanguageProvider, LocalizationContext };
